
import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';

const ExportProcessSection = () => {
  const { t } = useTranslation('home');

  const steps = [
    {
      number: "01",
      icon: "ri-discuss-line",
      title: t('process.step1.title'),
      description: t('process.step1.desc'),
      duration: t('process.step1.duration'),
    },
    {
      number: "02",
      icon: "ri-file-text-line",
      title: t('process.step2.title'),
      description: t('process.step2.desc'),
      duration: t('process.step2.duration'),
    },
    {
      number: "03",
      icon: "ri-shield-check-line",
      title: t('process.step3.title'),
      description: t('process.step3.desc'),
      duration: t('process.step3.duration'),
    },
    {
      number: "04",
      icon: "ri-ship-line",
      title: t('process.step4.title'),
      description: t('process.step4.desc'),
      duration: t('process.step4.duration'),
    },
    {
      number: "05",
      icon: "ri-store-3-line",
      title: t('process.step5.title'),
      description: t('process.step5.desc'),
      duration: t('process.step5.duration'),
    },
    {
      number: "06",
      icon: "ri-line-chart-line",
      title: t('process.step6.title'),
      description: t('process.step6.desc'),
      duration: t('process.step6.duration'),
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(steps.length - 1, index));
    setActiveIndex(clamped);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      goTo(activeIndex + (diff > 0 ? 1 : -1));
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.transform = `translateX(-${activeIndex * 100}%)`;
    }
  }, [activeIndex]);

  return (
    <section
      id="process"
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-b from-gray-950 to-black"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 bg-yellow-500/15 border border-yellow-400/30 rounded-full text-xs text-yellow-300 font-semibold uppercase tracking-wider mb-3 sm:mb-4">
            {t('process.title')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-5 font-serif">
            {t('process.sectionTitle')}{' '}
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
              {t('process.sectionTitleHighlight')}
            </span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed px-2">
            {t('process.description')}
          </p>
        </div>

        {/* ── MOBILE CAROUSEL ── */}
        <div className="block sm:hidden">
          {/* Step counter */}
          <div className="flex items-center justify-between mb-4 px-1">
            <span className="text-yellow-400 text-xs font-semibold">
              {t('process.stepLabel')} {activeIndex + 1} {t('process.stepOf')} {steps.length}
            </span>
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`transition-all rounded-full cursor-pointer ${
                    i === activeIndex
                      ? "w-5 h-2 bg-yellow-400"
                      : "w-2 h-2 bg-gray-600 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Slider viewport */}
          <div
            className="overflow-hidden rounded-2xl"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              ref={sliderRef}
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="min-w-full relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-2xl p-6 border border-yellow-500/20"
                >
                  <div className="absolute top-4 right-5 text-5xl font-black text-yellow-500/10 select-none">
                    {step.number}
                  </div>
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl mb-4">
                    <i className={`${step.icon} text-2xl text-black`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{step.description}</p>
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-yellow-400 text-sm"></i>
                    <span className="text-yellow-400 text-sm font-semibold">{step.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prev / Next buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => goTo(activeIndex - 1)}
              disabled={activeIndex === 0}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-yellow-500/30 text-yellow-400 text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors hover:bg-yellow-500/10 whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line text-base"></i> {t('process.prevBtn')}
            </button>
            <button
              onClick={() => goTo(activeIndex + 1)}
              disabled={activeIndex === steps.length - 1}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-yellow-500/30 text-yellow-400 text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors hover:bg-yellow-500/10 whitespace-nowrap"
            >
              {t('process.nextBtn')} <i className="ri-arrow-right-s-line text-base"></i>
            </button>
          </div>
        </div>

        {/* ── DESKTOP GRID (sm+) ── */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {steps.map((step, i) => (
            <div
              key={i}
              className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-6 md:p-8 border border-yellow-500/15 hover:border-yellow-400/40 transition-all hover:shadow-xl hover:shadow-yellow-500/10 hover:-translate-y-1"
            >
              <div className="absolute top-6 right-6 text-5xl font-black text-yellow-500/10 group-hover:text-yellow-500/20 transition-colors select-none">
                {step.number}
              </div>
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl mb-5 group-hover:scale-110 transition-transform">
                <i className={`${step.icon} text-2xl text-black`}></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mb-3">{step.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-5">{step.description}</p>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-yellow-400 text-sm"></i>
                <span className="text-yellow-400 text-sm font-semibold">{step.duration}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline note */}
        <div className="mt-8 sm:mt-10 md:mt-12 text-center px-4">
          <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-yellow-500/10 border border-yellow-400/20 rounded-full">
            <i className="ri-rocket-line text-yellow-400 text-base sm:text-lg"></i>
            <span className="text-gray-300 text-xs sm:text-sm">
              {t('process.timelineNote')}{' '}
              <strong className="text-yellow-400">{t('process.timelineValue')}</strong>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExportProcessSection;
